import React, { useState, useEffect } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { Box, Heading, Text, List, ListItem, useColorModeValue, Link, Checkbox, Button, HStack, Input, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import CollapsibleSection from './shared/CollapsibleSection';
import LoadingSpinner from './shared/LoadingSpinner';
import ErrorMessage from './shared/ErrorMessage';
import EmptyState from './shared/EmptyState';
import { useConfirmDialog } from './shared/ConfirmDialog';
import { useToast } from '../hooks/useToast';
import fetchWithAuth from '../utils/fetchWithAuth';

function PollsList() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPolls, setShowPolls] = useState(false);
  const [selectedPolls, setSelectedPolls] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const linkColor = useColorModeValue('theme.teal');
  const pollBoxBgColor = useColorModeValue('background.shade2');

  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const toast = useToast();

  const handleSelectPoll = (pollId) => {
    const newSelected = new Set(selectedPolls);
    if (newSelected.has(pollId)) {
      newSelected.delete(pollId);
    } else {
      newSelected.add(pollId);
    }
    setSelectedPolls(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPolls.size === polls.length) {
      setSelectedPolls(new Set());
    } else {
      setSelectedPolls(new Set(polls.map(p => p.id)));
    }
  };

  const handleBulkDelete = () => {
    confirm({
      title: 'Delete Selected Polls',
      message: `Are you sure you want to delete ${selectedPolls.size} poll(s)? This action cannot be undone.`,
      confirmText: 'Delete All',
      onConfirm: async () => {
        try {
          const deletePromises = Array.from(selectedPolls).map(pollId =>
            fetchWithAuth(`/api/polls/${pollId}/`, { method: 'DELETE' })
          );

          await Promise.all(deletePromises);

          setPolls(polls.filter(poll => !selectedPolls.has(poll.id)));
          setSelectedPolls(new Set());
          toast.showSuccess('Success', `${selectedPolls.size} poll(s) deleted`);
        } catch (error) {
          toast.showError('Error', 'Failed to delete some polls');
        }
      }
    });
  };

  useEffect(() => {
    fetchWithAuth("/api/polls/drafts")
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        setPolls(data.results || []);
        setLoading(false);
      })
      .catch(error => {
        setError(error.message);
        setLoading(false);
      });
  }, []);

  const togglePolls = () => {
    setShowPolls(!showPolls);
  };

  const filteredPolls = polls.filter(poll =>
    poll.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (poll.description && poll.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <>
      <ConfirmDialogComponent />
      <CollapsibleSection
        title="View Polls"
        isOpen={showPolls}
        onToggle={togglePolls}
      >
        {polls.length === 0 ? (
          <EmptyState message="No polls available." />
        ) : (
          <>
            <InputGroup mb={4}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search polls by title or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
            <HStack spacing={4} mb={4}>
              <Checkbox
                isChecked={selectedPolls.size === filteredPolls.length && filteredPolls.length > 0}
                onChange={handleSelectAll}
              >
                Select All
              </Checkbox>
              {selectedPolls.size > 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  Delete Selected ({selectedPolls.size})
                </Button>
              )}
            </HStack>
            {filteredPolls.length === 0 ? (
              <EmptyState message="No polls match your search." />
            ) : (
              <List spacing={4}>
                {filteredPolls.map(poll => (
                <ListItem key={poll.id} variant="poll-item" bg={pollBoxBgColor} p={4} borderRadius="md" border="1px solid" borderColor="border.default">
                  <HStack align="start" spacing={3}>
                    <Checkbox
                      isChecked={selectedPolls.has(poll.id)}
                      onChange={() => handleSelectPoll(poll.id)}
                      mt={1}
                    />
                    <Box flex="1">
                      <Heading as="h3" textStyle="h3" size="lg" mb={2}>
                        {poll.title}
                      </Heading>
                      <Text textStyle="p" mb={4}>
                        {poll.description}
                      </Text>
                      <Link as={ReactRouterLink} to={`/polls/${poll.id}/edit`} color={linkColor}>
                        Edit Poll
                      </Link>
                    </Box>
                  </HStack>
                </ListItem>
              ))}
              </List>
            )}
          </>
        )}
      </CollapsibleSection>
    </>
  );
}

export default PollsList;
